/**
 * @file 5 themes de stage (Forest, Desert, Tundra, Autumn, Blossom) : palettes de scene, tables d'apparition d'arbres PROPRES a chaque theme, decors, brouillard.
 */
import type { BiomeId } from '@shared/enums';
import type { BiomeDef } from './types';

export const BIOME_DEFS: Record<BiomeId, BiomeDef> = {
  forest: {
    id: 'forest',
    name: { fr: 'Foret', en: 'Forest' },
    // herbe vert franc plus sombre (le feuillage teal et le chat orange s'en detachent)
    ground: { base: '#4fbf4a', patch: '#44ad40', accent: '#a6e65a' },
    sky: '#8fd8ff',
    fog: { color: '#a8e3c4', near: 60, far: 120 },
    treeTable: [
      { targetId: 'tree_oak', weight: 100 },
      { targetId: 'tree_fir', weight: 60 },
      { targetId: 'tree_birch', weight: 70 },
      { targetId: 'tree_gold', weight: 4 },
      { targetId: 'tree_rose', weight: 1 },
    ],
    decorTable: [
      { targetId: 'decor_bush', density: 0.5 },
      { targetId: 'decor_rock', density: 0.15 },
    ],
  },
  desert: {
    id: 'desert',
    name: { fr: 'Desert', en: 'Desert' },
    ground: { base: '#f0cf7a', patch: '#e2bb62', accent: '#fbe3a2' },
    sky: '#ffe2a8',
    fog: { color: '#ffe0b0', near: 60, far: 120 },
    treeTable: [
      { targetId: 'tree_palm', weight: 100 },
      { targetId: 'tree_cactus', weight: 80 },
      { targetId: 'tree_palm_date', weight: 55 },
      { targetId: 'tree_gold', weight: 4 },
      { targetId: 'tree_rose', weight: 1 },
    ],
    decorTable: [
      { targetId: 'decor_rock', density: 0.3 },
      { targetId: 'decor_bush', density: 0.15 },
    ],
  },
  tundra: {
    id: 'tundra',
    name: { fr: 'Toundra', en: 'Tundra' },
    ground: { base: '#dcebf5', patch: '#c3d9ea', accent: '#ffffff' },
    sky: '#b8dcfa',
    fog: { color: '#d2e6f8', near: 60, far: 120 },
    treeTable: [
      { targetId: 'tree_fir_snow', weight: 100 },
      { targetId: 'tree_fir', weight: 40 },
      { targetId: 'tree_oak_frost', weight: 60 },
      { targetId: 'tree_gold', weight: 4 },
      { targetId: 'tree_rose', weight: 1 },
    ],
    decorTable: [
      { targetId: 'decor_rock', density: 0.3 },
      { targetId: 'decor_bush', density: 0.1 },
    ],
  },
  autumn: {
    id: 'autumn',
    name: { fr: 'Automne', en: 'Autumn' },
    ground: { base: '#dfb75e', patch: '#cca04a', accent: '#f2d88e' },
    sky: '#ffd6a0',
    fog: { color: '#ffdcb0', near: 60, far: 120 },
    treeTable: [
      { targetId: 'tree_maple', weight: 100 },
      { targetId: 'tree_oak_red', weight: 70 },
      { targetId: 'tree_fir_dry', weight: 60 },
      { targetId: 'tree_gold', weight: 4 },
      { targetId: 'tree_rose', weight: 1 },
    ],
    decorTable: [
      { targetId: 'decor_bush', density: 0.3 },
      { targetId: 'decor_rock', density: 0.2 },
    ],
  },
  blossom: {
    id: 'blossom',
    name: { fr: 'Cerisiers en fleurs', en: 'Blossom grove' },
    ground: { base: '#a9d98a', patch: '#94c977', accent: '#ffc2da' },
    sky: '#ffd3ea',
    fog: { color: '#fbd8ea', near: 60, far: 120 },
    treeTable: [
      { targetId: 'tree_blossom', weight: 100 },
      { targetId: 'tree_blossom_white', weight: 70 },
      { targetId: 'tree_birch', weight: 40 },
      { targetId: 'tree_gold', weight: 4 },
      { targetId: 'tree_rose', weight: 1 },
    ],
    decorTable: [
      { targetId: 'decor_bush', density: 0.4 },
      { targetId: 'decor_rock', density: 0.1 },
    ],
  },
};

/** Ordre de rotation des themes : stage 1 = forest, stage 2 = desert, stage 3 = tundra, stage 4 = autumn, stage 5 = blossom, puis on recommence. */
export const BIOME_ORDER: readonly BiomeId[] = ['forest', 'desert', 'tundra', 'autumn', 'blossom'];
